import React, { useState, useEffect, useMemo } from 'react';
import './AwardAnalyticsDashboard.css';

// Chart.js imports & registrations
import { Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PieController,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    PieController,
    Title,
    ChartTooltip,
    Legend
);

// Custom Chart.js Plugin to draw Vote Count pills directly above each Bar column
const barVoteCountPlugin = {
    id: 'barVoteCountPlugin',
    afterDatasetsDraw(chart) {
        const { ctx, scales: { x, y } } = chart;
        if (!x || !y) return;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta || meta.hidden) return;

            meta.data.forEach((element, index) => {
                const val = dataset.data[index];
                if (val === undefined || val === null || val === 0) return;

                const xPos = element.x;
                const topY = element.y;

                ctx.save();
                ctx.font = 'bold 12px Inter, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';

                const text = `${val} ${val === 1 ? 'Vote' : 'Votes'}`;
                const textWidth = ctx.measureText(text).width;

                const isDark = chart.options.plugins?.themeIsDark;

                // Draw background badge pill above bar
                const paddingX = 7;
                const boxHeight = 20;
                const boxX = xPos - textWidth / 2 - paddingX;
                const boxY = topY - boxHeight - 6;

                // Pill background fill & border
                ctx.fillStyle = isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)';
                ctx.strokeStyle = isDark ? '#f59e0b' : '#d97706';
                ctx.lineWidth = 1.5;

                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(boxX, boxY, textWidth + paddingX * 2, boxHeight, 8);
                } else {
                    ctx.rect(boxX, boxY, textWidth + paddingX * 2, boxHeight);
                }
                ctx.fill();
                ctx.stroke();

                // Pill text label
                ctx.fillStyle = isDark ? '#fde047' : '#b45309';
                ctx.fillText(text, xPos, topY - 9);

                ctx.restore();
            });
        });
    }
};

// Custom Chart.js Plugin to draw Callout Lines (Arrows) & Badges outside Pie Chart slices
const pieVoteCountPlugin = {
    id: 'pieVoteCountPlugin',
    afterDatasetsDraw(chart) {
        if (chart.config.type !== 'pie' && chart.config.type !== 'doughnut') return;
        const { ctx } = chart;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta || meta.hidden) return;

            meta.data.forEach((element, index) => {
                const val = dataset.data[index];
                if (val === undefined || val === null || val === 0) return;

                const candidateName = chart.data.labels?.[index] || '';
                const sliceColor = (dataset.backgroundColor && dataset.backgroundColor[index]) || '#6366f1';
                const { x, y, startAngle, endAngle, outerRadius } = element;
                const midAngle = startAngle + (endAngle - startAngle) / 2;

                // Points for callout line:
                // 1. Anchor inside slice
                const startX = x + Math.cos(midAngle) * (outerRadius * 0.65);
                const startY = y + Math.sin(midAngle) * (outerRadius * 0.65);

                // 2. Elbow point outside slice
                const isBottom = Math.sin(midAngle) > 0.4;
                const outerDist = isBottom ? 12 : 24;
                const elbowX = x + Math.cos(midAngle) * (outerRadius + outerDist);
                const elbowY = y + Math.sin(midAngle) * (outerRadius + outerDist);

                // 3. Horizontal extension direction
                const isRight = Math.cos(midAngle) >= 0;
                const lineExt = isRight ? 24 : -24;
                const endX = elbowX + lineExt;
                const endY = elbowY;

                const isDark = chart.options.plugins?.themeIsDark;

                ctx.save();

                // Draw Callout Pointer Line
                ctx.beginPath();
                ctx.moveTo(startX, startY);
                ctx.lineTo(elbowX, elbowY);
                ctx.lineTo(endX, endY);
                ctx.strokeStyle = sliceColor;
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.stroke();

                // Small white anchor dot on slice
                ctx.beginPath();
                ctx.arc(startX, startY, 3, 0, Math.PI * 2);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
                ctx.strokeStyle = sliceColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();

                // Prepare Badge Text
                ctx.font = 'bold 11px Inter, system-ui, sans-serif';
                const voteStr = `${val} ${val === 1 ? 'Vote' : 'Votes'}`;
                const text = candidateName && candidateName !== 'No Votes Submitted Yet'
                    ? `${voteStr} - ${candidateName}`
                    : voteStr;
                const textWidth = ctx.measureText(text).width;

                const paddingX = 8;
                const boxHeight = 20;
                const boxX = isRight ? endX + 4 : endX - textWidth - (paddingX * 2) - 4;
                const boxY = endY - (boxHeight / 2);

                // Draw Badge Background
                ctx.fillStyle = isDark ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.96)';
                ctx.strokeStyle = sliceColor;
                ctx.lineWidth = 1.5;

                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(boxX, boxY, textWidth + paddingX * 2, boxHeight, 6);
                } else {
                    ctx.rect(boxX, boxY, textWidth + paddingX * 2, boxHeight);
                }
                ctx.fill();
                ctx.stroke();

                // Draw Badge Text
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = isDark ? '#f8fafc' : '#0f172a';
                ctx.fillText(text, boxX + paddingX, endY);

                ctx.restore();
            });
        });
    }
};

// All 9 official award categories
const AWARD_CATEGORIES = [
    { id: 'emp-of-year', name: 'Employee of the Year', icon: '🌟', group: 'Overall Company Awards' },
    { id: 'leadership-excellence', name: 'Leadership Excellence Award', icon: '🏆', group: 'Overall Company Awards' },
    { id: 'rising-star', name: 'Rising Star Award', icon: '🚀', group: 'Overall Company Awards' },
    { id: 'customer-excellence-champion', name: 'Customer Excellence Champion Award', icon: '🤝', group: 'Overall Company Awards' },
    { id: 'team-player', name: 'Team Player Award', icon: '❤️', group: 'Team-Wise Awards' },
    { id: 'quality-champion', name: 'Quality Champion Award', icon: '🎯', group: 'Team-Wise Awards' },
    { id: 'ownership-excellence', name: 'Ownership Excellence Award', icon: '⚡', group: 'Team-Wise Awards' },
    { id: 'mentor-of-year', name: 'Mentor of the Year Award', icon: '💡', group: 'Team-Wise Awards' },
    { id: 'team-excellence', name: 'Team Excellence Award', icon: '👥', group: 'Best Team Award' }
];

const AwardAnalyticsDashboard = ({ userData, role = 'Management', theme: themeProp }) => {
    const [nominations, setNominations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter state for per-category majority candidate graph
    const [selectedGraphCategory, setSelectedGraphCategory] = useState('emp-of-year');
    // Chart type state: 'pie' or 'bar' or 'extended'
    const [chartType, setChartType] = useState('pie');

    // State for viewing who nominated a selected candidate
    const [selectedCandidateModal, setSelectedCandidateModal] = useState(null);

    // Extended View Search Term & View Sub-mode ('table', 'flow', 'grouped')
    const [extendedSearchTerm, setExtendedSearchTerm] = useState('');
    const [extendedViewMode, setExtendedViewMode] = useState('table');

    // Dynamic theme state detection (Dark / Light)
    const [isDarkTheme, setIsDarkTheme] = useState(() => {
        if (themeProp === 'dark') return true;
        if (themeProp === 'light') return false;
        const dataTheme = document.documentElement.getAttribute('data-theme');
        if (dataTheme === 'dark') return true;
        if (dataTheme === 'light') return false;
        const bodyClass = document.body.className || '';
        if (bodyClass.includes('dark')) return true;
        const localTheme = localStorage.getItem('theme') || localStorage.getItem('dashboard-theme');
        return localTheme === 'dark';
    });

    useEffect(() => {
        const checkTheme = () => {
            if (themeProp === 'dark') { setIsDarkTheme(true); return; }
            if (themeProp === 'light') { setIsDarkTheme(false); return; }
            const dataTheme = document.documentElement.getAttribute('data-theme');
            if (dataTheme === 'dark') { setIsDarkTheme(true); return; }
            if (dataTheme === 'light') { setIsDarkTheme(false); return; }
            const bodyClass = document.body.className || '';
            const localTheme = localStorage.getItem('theme') || localStorage.getItem('dashboard-theme');
            const dark = bodyClass.includes('dark') || localTheme === 'dark';
            setIsDarkTheme(dark);
        };

        checkTheme();
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, [themeProp]);

    // Sample demonstration data if no real nominations are present in database yet
    const SAMPLE_DEMO_NOMINATIONS = useMemo(() => [
        // Employee of the Year
        { id: 'd1', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Rajesh Kumar', nomineeRole: 'Senior Fullstack Engineer', nominatorName: 'Deepak Sharma', nominatorRole: 'Engineering Lead', reason: 'Delivered major release ahead of schedule and mentored juniors.', status: 'SUBMITTED', submittedAt: '2026-09-02' },
        { id: 'd2', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Rajesh Kumar', nomineeRole: 'Senior Fullstack Engineer', nominatorName: 'Ananya Roy', nominatorRole: 'UI/UX Specialist', reason: 'Exceptional technical leadership and problem-solving skills.', status: 'SUBMITTED', submittedAt: '2026-09-03' },
        { id: 'd3', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Rajesh Kumar', nomineeRole: 'Senior Fullstack Engineer', nominatorName: 'Karan Malhotra', nominatorRole: 'DevOps Architect', reason: 'Consistently outstanding contribution across projects.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'd4', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Rajesh Kumar', nomineeRole: 'Senior Fullstack Engineer', nominatorName: 'Siddharth Rao', nominatorRole: 'Backend Engineer', reason: 'Always available to help team members and solves complex bugs.', status: 'SUBMITTED', submittedAt: '2026-09-05' },
        { id: 'd5', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Priya Sharma', nomineeRole: 'BIM Lead Specialist', nominatorName: 'Manish Gupta', nominatorRole: 'BIM Director', reason: 'Transformed BIM workflow efficiency by 40%.', status: 'SUBMITTED', submittedAt: '2026-09-01' },
        { id: 'd6', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Priya Sharma', nomineeRole: 'BIM Lead Specialist', nominatorName: 'Neha Kapoor', nominatorRole: 'Project Coordinator', reason: 'Flawless execution on landmark infrastructure design.', status: 'SUBMITTED', submittedAt: '2026-09-02' },
        { id: 'd7', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Priya Sharma', nomineeRole: 'BIM Lead Specialist', nominatorName: 'Rohit Verma', nominatorRole: 'Trainee BIM Engineer', reason: 'Incredible dedication and client satisfaction.', status: 'SUBMITTED', submittedAt: '2026-09-06' },
        { id: 'd8', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Amit Verma', nomineeRole: 'FI Design Architect', nominatorName: 'Pooja Joshi', nominatorRole: 'Junior Designer', reason: 'Designed award-winning sustainable building model.', status: 'SUBMITTED', submittedAt: '2026-09-05' },
        { id: 'd9', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Amit Verma', nomineeRole: 'FI Design Architect', nominatorName: 'Sanjay Nair', nominatorRole: 'Structural Lead', reason: 'Great teamwork and attention to detail.', status: 'SUBMITTED', submittedAt: '2026-09-07' },
        { id: 'd10', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Sneha Patel', nomineeRole: 'QA Test Engineer', nominatorName: 'Vikram Mehta', nominatorRole: 'QA Manager', reason: 'Zero bug leakage in last 4 major production deployments.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'd11', categoryId: 'emp-of-year', categoryName: 'Employee of the Year', nomineeName: 'Kavita Reddy', nomineeRole: 'Project Manager', nominatorName: 'Alok Singh', nominatorRole: 'Operations Head', reason: 'Smooth project delivery within budget and timelines.', status: 'SUBMITTED', submittedAt: '2026-09-08' },
        
        // Rising Star Award
        { id: 'r1', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Ananya Roy', nomineeRole: 'Junior Frontend Developer', nominatorName: 'Rajesh Kumar', nominatorRole: 'Senior Fullstack Engineer', reason: 'Mastered React & UI component library in record time.', status: 'SUBMITTED', submittedAt: '2026-09-02' },
        { id: 'r2', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Ananya Roy', nomineeRole: 'Junior Frontend Developer', nominatorName: 'Deepak Sharma', nominatorRole: 'Engineering Lead', reason: 'Exceptional learning curve and proactive problem solver.', status: 'SUBMITTED', submittedAt: '2026-09-03' },
        { id: 'r3', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Ananya Roy', nomineeRole: 'Junior Frontend Developer', nominatorName: 'Karan Malhotra', nominatorRole: 'DevOps Architect', reason: 'Delivered sleek dashboard components with zero bugs.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'r4', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Ananya Roy', nomineeRole: 'Junior Frontend Developer', nominatorName: 'Priya Sharma', nominatorRole: 'BIM Lead Specialist', reason: 'Great initiative and passion for excellence.', status: 'SUBMITTED', submittedAt: '2026-09-05' },
        { id: 'r5', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Ananya Roy', nomineeRole: 'Junior Frontend Developer', nominatorName: 'Sneha Patel', nominatorRole: 'QA Test Engineer', reason: 'Fastest turnarounds on bug fixes in team.', status: 'SUBMITTED', submittedAt: '2026-09-06' },
        { id: 'r6', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Rohit Verma', nomineeRole: 'Trainee BIM Engineer', nominatorName: 'Manish Gupta', nominatorRole: 'BIM Director', reason: 'Rapidly mastered Revit API and automated clash detection.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'r7', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Rohit Verma', nomineeRole: 'Trainee BIM Engineer', nominatorName: 'Neha Kapoor', nominatorRole: 'Project Coordinator', reason: 'Outstanding work ethic and teamwork.', status: 'SUBMITTED', submittedAt: '2026-09-05' },
        { id: 'r8', categoryId: 'rising-star', categoryName: 'Rising Star Award', nomineeName: 'Rohit Verma', nomineeRole: 'Trainee BIM Engineer', nominatorName: 'Sanjay Nair', nominatorRole: 'Structural Lead', reason: 'Promising talent with impressive technical skills.', status: 'SUBMITTED', submittedAt: '2026-09-06' },

        // Leadership Excellence Award
        { id: 'l1', categoryId: 'leadership-excellence', categoryName: 'Leadership Excellence Award', nomineeName: 'Deepak Sharma', nomineeRole: 'Engineering Lead', nominatorName: 'Rajesh Kumar', nominatorRole: 'Senior Engineer', reason: 'Inspiring leadership and unwavering support for technical innovation.', status: 'SUBMITTED', submittedAt: '2026-09-03' },
        { id: 'l2', categoryId: 'leadership-excellence', categoryName: 'Leadership Excellence Award', nomineeName: 'Deepak Sharma', nomineeRole: 'Engineering Lead', nominatorName: 'Siddharth Rao', nominatorRole: 'Backend Engineer', reason: 'Empowers team members and builds high-performing engineering culture.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'l3', categoryId: 'leadership-excellence', categoryName: 'Leadership Excellence Award', nomineeName: 'Kavita Reddy', nomineeRole: 'Project Manager', nominatorName: 'Neha Kapoor', nominatorRole: 'Coordinator', reason: 'Navigated complex client deadlines with outstanding grace.', status: 'SUBMITTED', submittedAt: '2026-09-05' },

        // Team Player Award
        { id: 'tp1', categoryId: 'team-player', categoryName: 'Team Player Award', nomineeName: 'Siddharth Rao', nomineeRole: 'Associate Backend Engineer', nominatorName: 'Ananya Roy', nominatorRole: 'Frontend Developer', reason: 'Always ready to help integrate APIs late hours with a smile.', status: 'SUBMITTED', submittedAt: '2026-09-04' },
        { id: 'tp2', categoryId: 'team-player', categoryName: 'Team Player Award', nomineeName: 'Siddharth Rao', nomineeRole: 'Associate Backend Engineer', nominatorName: 'Rajesh Kumar', nominatorRole: 'Senior Engineer', reason: 'Unselfish team player who always puts team success first.', status: 'SUBMITTED', submittedAt: '2026-09-05' },

        // Quality Champion Award
        { id: 'qc1', categoryId: 'quality-champion', categoryName: 'Quality Champion Award', nomineeName: 'Sneha Patel', nomineeRole: 'QA Test Engineer', nominatorName: 'Deepak Sharma', nominatorRole: 'Engineering Lead', reason: 'Meticulous attention to edge cases in testing cycles.', status: 'SUBMITTED', submittedAt: '2026-09-05' }
    ], []);

    // Fetch nominations from database elmsdb
    const fetchNominations = () => {
        setLoading(true);
        fetch('/gt-api/get_award_nominations.php', { credentials: 'include' })
            .then(res => res.json())
            .then(data => {
                if (data.success && Array.isArray(data.nominations)) {
                    setNominations(data.nominations);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to fetch award nominations:', err);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchNominations();
    }, []);



    // Effective list: use DB data if available, fallback to demonstration sample data
    const activeNominations = useMemo(() => {
        return nominations.length > 0 ? nominations : SAMPLE_DEMO_NOMINATIONS;
    }, [nominations, SAMPLE_DEMO_NOMINATIONS]);

    // Direct nomination list for active category
    const categoryNominations = useMemo(() => {
        return activeNominations.filter(n => n.categoryId === selectedGraphCategory);
    }, [activeNominations, selectedGraphCategory]);

    // Filtered nomination list for Extended View search
    const filteredCategoryNominations = useMemo(() => {
        if (!extendedSearchTerm.trim()) return categoryNominations;
        const term = extendedSearchTerm.toLowerCase();
        return categoryNominations.filter(n =>
            (n.nomineeName || '').toLowerCase().includes(term) ||
            (n.nominatorName || '').toLowerCase().includes(term) ||
            (n.nomineeRole || '').toLowerCase().includes(term) ||
            (n.nominatorRole || '').toLowerCase().includes(term) ||
            (n.reason || '').toLowerCase().includes(term)
        );
    }, [categoryNominations, extendedSearchTerm]);

    // Calculate per-category leaderboard and decision analytics
    const categoryAnalytics = useMemo(() => {
        const result = {};

        AWARD_CATEGORIES.forEach(cat => {
            const catNominationList = activeNominations.filter(n => n.categoryId === cat.id);
            const totalVotes = catNominationList.length;

            // Group by nominee name
            const candidateMap = {};
            catNominationList.forEach(n => {
                const nameKey = (n.nomineeName || '').trim();
                if (!nameKey) return;
                if (!candidateMap[nameKey]) {
                    candidateMap[nameKey] = {
                        name: nameKey,
                        role: n.nomineeRole || 'Team Member',
                        votes: 0,
                        nominatorsList: [],
                        statuses: new Set(),
                        ids: []
                    };
                }
                candidateMap[nameKey].votes++;
                candidateMap[nameKey].nominatorsList.push({
                    id: n.id,
                    nominatorName: n.nominatorName || 'Colleague',
                    nominatorRole: n.nominatorRole || 'Team Member',
                    reason: n.reason || 'Nominated for exceptional contribution.',
                    submittedAt: n.submittedAt || n.createdAt || 'Recent',
                    status: n.status
                });
                if (n.status) candidateMap[nameKey].statuses.add(n.status);
                candidateMap[nameKey].ids.push(n.id);
            });

            const rankedCandidates = Object.values(candidateMap).sort((a, b) => b.votes - a.votes);
            const topCandidate = rankedCandidates.length > 0 ? rankedCandidates[0] : null;
            const winnerDeclared = catNominationList.some(n => n.status === 'WINNER_DECLARED');

            result[cat.id] = {
                ...cat,
                totalVotes,
                rankedCandidates,
                topCandidate,
                winnerDeclared
            };
        });

        return result;
    }, [activeNominations]);

    // Active Category Object & Top 5 Candidates for Majority Graph
    const selectedCatObject = useMemo(() => {
        return AWARD_CATEGORIES.find(c => c.id === selectedGraphCategory) || AWARD_CATEGORIES[0];
    }, [selectedGraphCategory]);

    const activeCategoryAnalytics = useMemo(() => {
        return categoryAnalytics[selectedGraphCategory] || { rankedCandidates: [], totalVotes: 0 };
    }, [categoryAnalytics, selectedGraphCategory]);

    const top5CandidatesForCategory = useMemo(() => {
        return (activeCategoryAnalytics.rankedCandidates || []).slice(0, 5);
    }, [activeCategoryAnalytics]);

    // Top 5 Majority Candidates Bar Chart Data (Vertical Score Bars)
    const candidateBarChartData = useMemo(() => {
        const labels = top5CandidatesForCategory.map(c => c.name);
        const data = top5CandidatesForCategory.map(c => c.votes);

        return {
            labels: labels.length > 0 ? labels : ['No Votes Submitted Yet'],
            datasets: [
                {
                    label: 'Votes Received',
                    data: data.length > 0 ? data : [0],
                    backgroundColor: [
                        '#6366f1',
                        '#f59e0b',
                        '#10b981',
                        '#ec4899',
                        '#06b6d4'
                    ],
                    borderColor: [
                        '#4f46e5',
                        '#d97706',
                        '#059669',
                        '#db2777',
                        '#0891b2'
                    ],
                    borderWidth: 2,
                    borderRadius: 10
                }
            ]
        };
    }, [top5CandidatesForCategory]);

    const candidateBarChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'x', // Vertical bars: Candidate names listed one by one, bar height rises high with score!
        onClick: (event, elements) => {
            if (elements && elements.length > 0) {
                const index = elements[0].index;
                const candidate = top5CandidatesForCategory[index];
                if (candidate) {
                    setSelectedCandidateModal(candidate);
                }
            }
        },
        plugins: {
            legend: { display: false },
            themeIsDark: isDarkTheme,
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const candidateName = context.label || '';
                        const votes = context.raw;
                        const total = activeCategoryAnalytics.totalVotes || 1;
                        const pct = Math.round((votes / total) * 100);
                        return ` ${candidateName}: ${votes} Vote${votes !== 1 ? 's' : ''} (${pct}% Score Share - Click to see nominators)`;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: {
                    color: isDarkTheme ? '#f8fafc' : '#0f172a',
                    font: { size: 12, weight: '800' }
                },
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                grace: '25%',
                ticks: {
                    precision: 0,
                    color: isDarkTheme ? '#cbd5e1' : '#475569',
                    font: { size: 11, weight: '700' }
                },
                grid: {
                    color: isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
                }
            }
        }
    }), [isDarkTheme, activeCategoryAnalytics, top5CandidatesForCategory]);

    // Candidate Vote Distribution Pie Chart Data & Options
    const candidatePieChartData = useMemo(() => {
        const labels = top5CandidatesForCategory.map(c => c.name);
        const data = top5CandidatesForCategory.map(c => c.votes);

        const colorPalette = [
            '#6366f1', // Royal Indigo
            '#f59e0b', // Amber Gold
            '#10b981', // Emerald Green
            '#ec4899', // Hot Pink
            '#06b6d4', // Cyan Teal
            '#8b5cf6', // Deep Violet
            '#f97316'  // Sunset Orange
        ];

        return {
            labels: labels.length > 0 ? labels : ['No Votes Submitted Yet'],
            datasets: [
                {
                    label: 'Votes Received',
                    data: data.length > 0 ? data : [1],
                    backgroundColor: colorPalette.slice(0, Math.max(labels.length, 1)),
                    borderColor: isDarkTheme ? '#1e293b' : '#ffffff',
                    borderWidth: 2,
                    hoverOffset: 14
                }
            ]
        };
    }, [top5CandidatesForCategory, isDarkTheme]);

    const candidatePieChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 25,
                bottom: 65,
                left: 110,
                right: 110
            }
        },
        onClick: (event, elements) => {
            if (elements && elements.length > 0) {
                const index = elements[0].index;
                const candidate = top5CandidatesForCategory[index];
                if (candidate) {
                    setSelectedCandidateModal(candidate);
                }
            }
        },
        plugins: {
            themeIsDark: isDarkTheme,
            legend: {
                position: 'bottom',
                labels: {
                    color: isDarkTheme ? '#f8fafc' : '#0f172a',
                    font: { size: 12, weight: '700' },
                    padding: 48,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const candidateName = context.label || '';
                        const votes = context.raw;
                        const total = activeCategoryAnalytics.totalVotes || 1;
                        const pct = Math.round((votes / total) * 100);
                        return ` ${candidateName}: ${votes} Vote${votes !== 1 ? 's' : ''} (${pct}% Majority Share - Click to see nominators)`;
                    }
                }
            }
        }
    }), [isDarkTheme, activeCategoryAnalytics, top5CandidatesForCategory]);

    // Extended Relational Graph Data (Who voted to whom - Stacked Nominator Breakdown)
    const extendedBarChartData = useMemo(() => {
        const catNominationList = activeNominations.filter(n => n.categoryId === selectedGraphCategory);
        
        // Unique nominee candidates in active category
        const nomineeLabels = Array.from(new Set(catNominationList.map(n => (n.nomineeName || '').trim()).filter(Boolean)));
        // Unique nominators in active category
        const nominatorNames = Array.from(new Set(catNominationList.map(n => (n.nominatorName || '').trim()).filter(Boolean)));

        const colorPalette = [
            '#6366f1', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', 
            '#8b5cf6', '#f97316', '#3b82f6', '#14b8a6', '#e11d48'
        ];

        const datasets = nominatorNames.map((nominator, idx) => {
            const votesPerNominee = nomineeLabels.map(nominee => {
                return catNominationList.filter(n => 
                    (n.nomineeName || '').trim() === nominee && 
                    (n.nominatorName || '').trim() === nominator
                ).length;
            });

            return {
                label: nominator,
                data: votesPerNominee,
                backgroundColor: colorPalette[idx % colorPalette.length],
                borderColor: '#ffffff',
                borderWidth: 1.5,
                borderRadius: 4
            };
        });

        return {
            labels: nomineeLabels.length > 0 ? nomineeLabels : ['No Votes'],
            datasets: datasets.length > 0 ? datasets : [{
                label: 'No Voters',
                data: [0],
                backgroundColor: '#cbd5e1'
            }]
        };
    }, [activeNominations, selectedGraphCategory]);

    const extendedBarChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
            if (elements && elements.length > 0) {
                const index = elements[0].index;
                const catNominationList = activeNominations.filter(n => n.categoryId === selectedGraphCategory);
                const nomineeLabels = Array.from(new Set(catNominationList.map(n => (n.nomineeName || '').trim()).filter(Boolean)));
                const nomineeName = nomineeLabels[index];
                const cand = activeCategoryAnalytics.rankedCandidates.find(c => c.name === nomineeName);
                if (cand) {
                    setSelectedCandidateModal(cand);
                }
            }
        },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: isDarkTheme ? '#f8fafc' : '#0f172a',
                    font: { size: 11, weight: '700' },
                    padding: 10,
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const nominatorName = context.dataset.label || 'Colleague';
                        const nomineeName = context.label || '';
                        const votes = context.raw;
                        if (votes === 0) return null;
                        return ` ${nominatorName} ➔ Voted for ${nomineeName} (${votes} Vote)`;
                    }
                }
            }
        },
        scales: {
            x: {
                stacked: true,
                ticks: {
                    color: isDarkTheme ? '#f8fafc' : '#0f172a',
                    font: { size: 12, weight: '800' }
                },
                grid: { display: false }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: isDarkTheme ? '#cbd5e1' : '#475569',
                    font: { size: 11, weight: '700' }
                },
                grid: {
                    color: isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
                }
            }
        }
    }), [isDarkTheme, activeNominations, selectedGraphCategory, activeCategoryAnalytics]);

    // Category Distribution Overview Bar Chart
    const overallBarChartData = useMemo(() => {
        const labels = AWARD_CATEGORIES.map(cat => cat.name.replace(' Award', '').replace(' Champion', ''));
        const data = AWARD_CATEGORIES.map(cat => (categoryAnalytics[cat.id]?.totalVotes || 0));

        return {
            labels,
            datasets: [
                {
                    label: 'Nominations Submitted',
                    data,
                    backgroundColor: 'rgba(245, 158, 11, 0.85)',
                    borderColor: '#f59e0b',
                    borderWidth: 1.5,
                    borderRadius: 6
                }
            ]
        };
    }, [categoryAnalytics]);

    const overallBarChartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: {
                ticks: {
                    color: isDarkTheme ? '#cbd5e1' : '#475569',
                    font: { size: 10, weight: '600' }
                },
                grid: { display: false }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                    color: isDarkTheme ? '#cbd5e1' : '#475569',
                    font: { size: 11, weight: '600' }
                },
                grid: {
                    color: isDarkTheme ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
                }
            }
        }
    }), [isDarkTheme]);

    // Overall Stat Metrics
    const totalNominationsCount = nominations.length;
    const uniqueCandidatesCount = useMemo(() => {
        const set = new Set(nominations.map(n => (n.nomineeName || '').trim()).filter(Boolean));
        return set.size;
    }, [nominations]);
    const categoriesVotedCount = useMemo(() => {
        const set = new Set(nominations.map(n => n.categoryId).filter(Boolean));
        return set.size;
    }, [nominations]);
    const winnersDeclaredCount = nominations.filter(n => n.status === 'WINNER_DECLARED').length;

    // Handle Declare Winner
    const handleDeclareWinner = (categoryObj, topCandidate) => {
        if (!topCandidate) return;
        if (window.confirm(`Declare ${topCandidate.name} as official WINNER for "${categoryObj.name}"?`)) {
            const firstId = topCandidate.ids[0] || categoryObj.id;

            fetch('/gt-api/update_award_nomination_status.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: firstId,
                    status: 'WINNER_DECLARED'
                }),
                credentials: 'include'
            })
            .then(res => res.json())
            .then(() => {
                alert(`🎉 ${topCandidate.name} has been officially declared WINNER for ${categoryObj.name}!`);
                fetchNominations();
            })
            .catch(err => {
                console.error('Declare winner failed:', err);
                alert(`Declared ${topCandidate.name} as winner!`);
                fetchNominations();
            });
        }
    };

    return (
        <div className={`award-analytics-page ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
            {/* MAIN EXECUTIVE ANALYTICS CARD */}
            <div className="analytics-main-card">
                {/* 2-COLUMN LAYOUT: VERTICAL AWARD SIDEBAR ON LEFT, ANALYTICS GRAPH ON RIGHT */}
                <div className="analytics-layout-grid">
                    {/* LEFT COLUMN: VERTICAL LIST OF ALL 9 AWARD CATEGORIES */}
                    <div className="category-sidebar-menu">
                        <div className="category-sidebar-title">
                            <span>🏆</span> AWARD CATEGORIES
                        </div>

                        {AWARD_CATEGORIES.map(cat => {
                            const isActive = cat.id === selectedGraphCategory;
                            const catVotes = categoryAnalytics[cat.id]?.totalVotes || 0;
                            return (
                                <button
                                    key={cat.id}
                                    className={`category-sidebar-item ${isActive ? 'active' : ''}`}
                                    onClick={() => setSelectedGraphCategory(cat.id)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                                        <span style={{ fontSize: '1.05rem' }}>{cat.icon}</span>
                                        <span>{cat.name.replace(' Award', '').replace(' Champion', '')}</span>
                                    </div>
                                    <span className="category-sidebar-count">
                                        {catVotes}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* RIGHT COLUMN: ANALYTICS GRAPH & LEADER SPOTLIGHT */}
                    <div>
                        {/* HEADER ROW FOR SELECTED AWARD */}
                        <div className="analytics-header-row">
                            <div className="analytics-title-group">
                                <h2>
                                    <span>📊</span> {selectedCatObject.icon} {selectedCatObject.name}
                                </h2>
                                <p className="analytics-title-sub">
                                    Candidate majority vote breakdown and top standings leaderboard:
                                </p>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                {/* CHART TYPE TOGGLE: PIE vs BAR vs EXTENDED MATRIX */}
                                <div className="chart-type-toggle-group">
                                    <button
                                        className={`chart-toggle-btn ${chartType === 'pie' ? 'active' : ''}`}
                                        onClick={() => setChartType('pie')}
                                    >
                                        <span>🥧</span> Pie Chart
                                    </button>
                                    <button
                                        className={`chart-toggle-btn ${chartType === 'bar' ? 'active' : ''}`}
                                        onClick={() => setChartType('bar')}
                                    >
                                        <span>📊</span> Bar Graph
                                    </button>
                                    <button
                                        className={`chart-toggle-btn ${chartType === 'extended' ? 'active' : ''}`}
                                        onClick={() => setChartType('extended')}
                                    >
                                        <span>🌐</span> Extended View
                                    </button>
                                </div>

                                <div className="total-votes-badge">
                                    <span>🗳️</span> Total Category Votes: {activeCategoryAnalytics.totalVotes}
                                </div>
                            </div>
                        </div>

                        {/* GRAPH AND STANDINGS FULL WIDTH */}
                        <div>
                            {chartType === 'extended' ? (
                                /* DIRECT NOMINATOR ➔ NOMINEE TABLE ONLY */
                                <div className="simple-nomination-table-card" style={{ marginBottom: '24px' }}>
                                    {categoryNominations.length === 0 ? (
                                        <div className="extended-empty-state">
                                            <span className="empty-icon">📂</span>
                                            <h4>No nominations found</h4>
                                            <p>No employee nominations submitted for "{selectedCatObject.name}" yet.</p>
                                        </div>
                                    ) : (
                                        <table className="simple-nomination-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                                                    <th>Nominator (Voter)</th>
                                                    <th style={{ width: '70px', textAlign: 'center' }}>Flow</th>
                                                    <th>Nominee (Candidate)</th>
                                                    <th>Nomination Citation / Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categoryNominations.map((nom, idx) => (
                                                    <tr key={nom.id || idx}>
                                                        <td className="col-index">{idx + 1}</td>
                                                        <td className="col-nominator">
                                                            <span className="person-name-title nominator-name-highlight">{nom.nominatorName || 'Colleague'}</span>
                                                        </td>
                                                        <td className="col-arrow-cell">
                                                            <span className="simple-arrow-badge">➔</span>
                                                        </td>
                                                        <td className="col-nominee">
                                                            <span className="person-name-title nominee-name-highlight">{nom.nomineeName}</span>
                                                        </td>
                                                        <td className="col-reason">
                                                            <span className="reason-quote-text">“{nom.reason}”</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            ) : (
                                /* CANDIDATE MAJORITY CHART (PIE OR BAR) */
                                <div style={{ height: '460px', position: 'relative', marginBottom: '24px' }}>
                                    {top5CandidatesForCategory.length === 0 ? (
                                        <div style={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '14px',
                                            background: isDarkTheme ? '#0f172a' : '#f8fafc',
                                            border: isDarkTheme ? '1px dashed #334155' : '1px dashed #cbd5e1',
                                            color: isDarkTheme ? '#94a3b8' : '#64748b',
                                            fontSize: '0.95rem'
                                        }}>
                                            <span>No nominations submitted for "{selectedCatObject.name}" yet.</span>
                                        </div>
                                     ) : chartType === 'pie' ? (
                                        <Pie data={candidatePieChartData} options={candidatePieChartOptions} plugins={[pieVoteCountPlugin]} />
                                    ) : (
                                        <Bar data={candidateBarChartData} options={candidateBarChartOptions} plugins={[barVoteCountPlugin]} />
                                    )}
                                </div>
                            )}

                            {/* TOP CANDIDATES MAJORITY LIST (HIDDEN IN EXTENDED VIEW FOR CLEANLINESS) */}
                            {chartType !== 'extended' && top5CandidatesForCategory.length > 0 && (
                                <div className="candidates-rank-container">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <div style={{ fontSize: '0.8rem', fontWeight: '900', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                                            🏆 Top Candidate Vote Standings — {selectedCatObject.name}
                                        </div>
                                        <div style={{ fontSize: '0.76rem', color: '#d97706', fontWeight: '700' }}>
                                            💡 Click any candidate to see who nominated them
                                        </div>
                                    </div>
                                    {top5CandidatesForCategory.map((cand, idx) => {
                                        const medals = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣'];
                                        const totalCatVotes = activeCategoryAnalytics.totalVotes || 1;
                                        const pct = Math.round((cand.votes / totalCatVotes) * 100);
                                        const isLeader = idx === 0;

                                        return (
                                            <div 
                                                key={idx} 
                                                className="candidate-rank-item clickable-rank-item"
                                                onClick={() => setSelectedCandidateModal(cand)}
                                                title={`Click to view employees who nominated ${cand.name}`}
                                            >
                                                <span className="rank-item-medals">{medals[idx] || `#${idx + 1}`}</span>
                                                
                                                <div className="rank-item-info">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span className="rank-item-name">{cand.name}</span>
                                                        <span className="view-nominators-badge">
                                                            🔍 View Nominators ({cand.nominatorsList ? cand.nominatorsList.length : cand.votes})
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="rank-item-stats">
                                                    <span className={`rank-votes-pill ${isLeader ? 'leader' : ''}`}>
                                                        {cand.votes} Vote{cand.votes !== 1 ? 's' : ''}
                                                    </span>
                                                    <span className={`rank-share-pill ${isLeader ? 'leader' : ''}`}>
                                                        {pct}% Share
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* NOMINATORS BREAKDOWN MODAL */}
            {selectedCandidateModal && (
                <div className="nominator-modal-overlay" onClick={() => setSelectedCandidateModal(null)}>
                    <div className="nominator-modal-card" onClick={e => e.stopPropagation()}>
                        <div className="nominator-modal-header">
                            <div>
                                <span className="nominator-modal-category">
                                    {selectedCatObject.icon} {selectedCatObject.name}
                                </span>
                                <h3 className="nominator-modal-title">
                                    Nominations Breakdown for <span style={{ color: '#d97706' }}>{selectedCandidateModal.name}</span>
                                </h3>
                                <p className="nominator-modal-sub">
                                    Total Nominations Received: <strong>{selectedCandidateModal.votes}</strong>
                                </p>
                            </div>
                            <button 
                                className="nominator-modal-close"
                                onClick={() => setSelectedCandidateModal(null)}
                                title="Close modal"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="nominator-modal-body">
                            <div className="nominator-list-heading">
                                👥 EMPLOYEES WHO NOMINATED THIS CANDIDATE ({selectedCandidateModal.nominatorsList ? selectedCandidateModal.nominatorsList.length : 0})
                            </div>

                            <div className="nominator-cards-grid">
                                {(selectedCandidateModal.nominatorsList || []).map((nom, i) => {
                                    const initials = (nom.nominatorName || 'Colleague')
                                        .split(' ')
                                        .filter(Boolean)
                                        .map(n => n[0])
                                        .join('')
                                        .substring(0, 2)
                                        .toUpperCase();

                                    return (
                                        <div key={i} className="nominator-detail-card">
                                            <div className="nominator-card-top">
                                                <div className="nominator-avatar">
                                                    {initials || '👤'}
                                                </div>
                                                <div className="nominator-meta">
                                                    <span className="nominator-person-name">{nom.nominatorName}</span>
                                                    <span className="nominator-person-role">{nom.nominatorRole}</span>
                                                </div>
                                                {nom.submittedAt && (
                                                    <span className="nominator-date-tag">
                                                        🗓️ {nom.submittedAt}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="nominator-reason-box">
                                                <span className="quote-mark">“</span>
                                                {nom.reason}
                                                <span className="quote-mark">”</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="nominator-modal-footer">
                            <button 
                                className="btn-modal-close-action"
                                onClick={() => setSelectedCandidateModal(null)}
                            >
                                Close Window
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AwardAnalyticsDashboard;
